import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  Skeleton,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  ExploreCards,
  FundSummary,
  IFundSummaryStats,
  PdfPreviewDialog,
  SimpleUpdatesList,
  SkeletonWrapper,
  type ISimpleUpdatesItem,
} from 'src/components/molecules'

interface IOverviewTemplateProps {
  userName: string
  currentDate: string
  isLoading?: boolean
  isFundSummaryLoading?: boolean
  isDocumentsLoading?: boolean
  fundSummaryStats?: IFundSummaryStats
  recentNewsItems: ISimpleUpdatesItem[]
  newDocumentItems: ISimpleUpdatesItem[]
  selectedDocumentBlobUrl?: string | null
  onDocumentPreviewOpen?: (item: ISimpleUpdatesItem) => void
  onDocumentPreviewClose?: () => void
}

export const OverviewTemplate = ({
  userName,
  currentDate,
  isLoading = false,
  isFundSummaryLoading = false,
  isDocumentsLoading = false,
  fundSummaryStats,
  recentNewsItems,
  newDocumentItems,
  selectedDocumentBlobUrl,
  onDocumentPreviewOpen,
  onDocumentPreviewClose,
}: IOverviewTemplateProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedDocument, setSelectedDocument] =
    useState<ISimpleUpdatesItem | null>(null)

  const handleRecentNewsClick = (item: ISimpleUpdatesItem): void => {
    navigate(`/portfolio-news/${item.id}`)
  }

  const handleNewDocumentClick = (item: ISimpleUpdatesItem): void => {
    if (!item.pdfUrl) {
      return
    }

    setSelectedDocument(item)
    onDocumentPreviewOpen?.(item)
  }

  const handleClosePreview = (): void => {
    setSelectedDocument(null)
    onDocumentPreviewClose?.()
  }

  return (
    <>
      <main className="bg-surface-page">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-5 py-8 md:gap-12 md:px-12 md:py-12 xl:px-0">
          <header className="flex flex-col gap-5 md:gap-3">
            <div className="flex flex-col gap-3">
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {currentDate}
              </Typography>
              <Typography
                variant={TypographyVariant.titleXl}
                weight={TypographyWeight.light}
                className="text-title-md text-content-primary md:text-title-xl"
              >
                {t('overview.welcomeBack', { name: userName })}
              </Typography>
            </div>
            <div className="h-px w-full bg-border-default md:bg-content-secondary md:opacity-20" />
          </header>

          <div className="flex flex-col gap-11 md:gap-12">
            <section className="flex flex-col gap-4 md:gap-8">
              <div className="flex flex-col gap-1">
                <Typography
                  variant={TypographyVariant.titleSm}
                  weight={TypographyWeight.light}
                  className="text-content-primary"
                >
                  {t('overview.sections.fundSummary')}
                </Typography>
                {fundSummaryStats?.summaryAsOf ? (
                  <Typography
                    variant={TypographyVariant.bodySm}
                    className="text-content-secondary"
                  >
                    {t('fundUpdates.summaryAsOf', {
                      date: fundSummaryStats.summaryAsOf,
                    })}
                  </Typography>
                ) : null}
              </div>
              <SkeletonWrapper
                isLoading={isFundSummaryLoading}
                skeleton={<Skeleton.SummaryCards count={4} />}
              >
                <FundSummary
                  items={fundSummaryStats?.fundSummaryItems ?? []}
                  emptyState={{
                    title: t('overview.emptyState.fundSummary.title'),
                    description: t('overview.emptyState.dataAvailableSoon'),
                  }}
                />
              </SkeletonWrapper>
            </section>

            <div className="grid grid-cols-1 gap-11 md:gap-12 lg:grid-cols-2">
              <section className="flex flex-col gap-4 md:gap-6">
                <Typography
                  variant={TypographyVariant.titleSm}
                  weight={TypographyWeight.light}
                  className="text-content-primary"
                >
                  {t('overview.sections.recentNews')}
                </Typography>
                <SkeletonWrapper
                  isLoading={isLoading}
                  skeleton={<Skeleton.SimpleList count={3} />}
                >
                  <SimpleUpdatesList
                    items={recentNewsItems}
                    onRowClick={handleRecentNewsClick}
                    emptyState={{
                      title: t('overview.emptyState.recentNews.title'),
                      description: t('overview.emptyState.dataAvailableSoon'),
                    }}
                  />
                </SkeletonWrapper>
              </section>

              <section className="flex flex-col gap-4 md:gap-6">
                <Typography
                  variant={TypographyVariant.titleSm}
                  weight={TypographyWeight.light}
                  className="text-content-primary"
                >
                  {t('overview.sections.newDocuments')}
                </Typography>
                <SkeletonWrapper
                  isLoading={isDocumentsLoading}
                  skeleton={<Skeleton.SimpleList count={3} />}
                >
                  <SimpleUpdatesList
                    items={newDocumentItems}
                    onRowClick={handleNewDocumentClick}
                    emptyState={{
                      title: t('overview.emptyState.newDocuments.title'),
                      description: t('overview.emptyState.dataAvailableSoon'),
                    }}
                  />
                </SkeletonWrapper>
              </section>
            </div>

            <ExploreCards />
          </div>
        </div>
      </main>
      {selectedDocument && (
        <PdfPreviewDialog
          isOpen={!!selectedDocument}
          onClose={handleClosePreview}
          title={selectedDocument.title}
          subtitle={selectedDocument.subtitle}
          pdfUrl={selectedDocumentBlobUrl ?? null}
        />
      )}
    </>
  )
}
